import { useCallback, useEffect, useState } from "react";
import { api, avatarUrl } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, X, Crown, Users } from "lucide-react";

const roleLabel = (r) => (r || "").replace(/_/g, " ");

export function ProjectMembers({ projectId }) {
  const [data, setData] = useState(null);
  const [users, setUsers] = useState([]);
  const [adding, setAdding] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [m, u] = await Promise.all([
      api.get(`/projects/${projectId}/members`),
      api.get("/users"),
    ]);
    setData(m.data);
    setUsers(u.data || []);
  }, [projectId]);

  useEffect(() => { load().catch(() => {}); }, [load]);

  if (!data) return null;

  const memberIds = new Set([data.manager?.user_id, ...(data.members || []).map((m) => m.user_id)]);
  const addable = users.filter((u) => u.active !== false && !memberIds.has(u.user_id));

  const addMember = async () => {
    if (!adding) return;
    setBusy(true);
    try {
      const { data: fresh } = await api.post(`/projects/${projectId}/members`, { user_id: adding });
      setData((d) => ({ ...fresh, can_manage: d.can_manage }));
      setAdding("");
      toast.success("Member added");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add member");
    } finally { setBusy(false); }
  };

  const removeMember = async (uid) => {
    setBusy(true);
    try {
      const { data: fresh } = await api.delete(`/projects/${projectId}/members/${uid}`);
      setData((d) => ({ ...fresh, can_manage: d.can_manage }));
      toast.success("Member removed");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove member");
    } finally { setBusy(false); }
  };

  const changePm = async (uid) => {
    setBusy(true);
    try {
      const { data: fresh } = await api.put(`/projects/${projectId}/manager`, { user_id: uid });
      setData((d) => ({ ...fresh, can_manage: d.can_manage }));
      toast.success("Project Manager updated");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not change PM");
    } finally { setBusy(false); }
  };

  const pmCandidates = users.filter((u) => ["admin", "project_coordinator", "project_manager"].includes(u.role) && u.user_id !== data.manager?.user_id);

  return (
    <Card className="p-6 border-slate-200" data-testid="project-members-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Users className="h-4 w-4 text-[#2563EB]" />
          <h3 className="text-sm font-semibold text-slate-900">Project Members</h3>
          <Badge variant="outline" className="text-[10px]">{(data.members?.length || 0) + 1}</Badge>
        </div>
        {!data.can_manage && <span className="text-[11px] text-slate-400">Only Admins, Coordinators and the PM can edit members</span>}
      </div>

      <div className="mt-4 space-y-2">
        {/* Project Manager */}
        <div className="flex items-center gap-3 rounded-lg border border-blue-100 bg-blue-50/50 px-3 py-2" data-testid="project-member-pm">
          <Avatar className="h-8 w-8">
            <AvatarImage src={avatarUrl(data.manager?.picture)} alt={data.manager?.name} />
            <AvatarFallback className="text-xs bg-[#2563EB] text-white">{(data.manager?.name || "?").slice(0, 2).toUpperCase()}</AvatarFallback>
          </Avatar>
          <div className="min-w-0">
            <div className="text-sm font-medium text-slate-900 truncate flex items-center gap-1.5">{data.manager?.name} <Crown className="h-3.5 w-3.5 text-amber-500" /></div>
            <div className="text-[11px] text-slate-500">Project Manager</div>
          </div>
          {data.can_manage && pmCandidates.length > 0 && (
            <div className="ml-auto">
              <Select value="" onValueChange={changePm} disabled={busy}>
                <SelectTrigger className="h-8 w-[150px] text-xs" data-testid="change-pm-select"><SelectValue placeholder="Reassign PM" /></SelectTrigger>
                <SelectContent>
                  {pmCandidates.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Members */}
        {(data.members || []).map((m) => (
          <div key={m.user_id} className="flex items-center gap-3 rounded-lg border border-slate-200 px-3 py-2" data-testid={`project-member-${m.user_id}`}>
            <Avatar className="h-8 w-8">
              <AvatarImage src={avatarUrl(m.picture)} alt={m.name} />
              <AvatarFallback className="text-xs bg-slate-700 text-white">{(m.name || "?").slice(0, 2).toUpperCase()}</AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <div className="text-sm font-medium text-slate-900 truncate">{m.name}</div>
              <div className="text-[11px] text-slate-500 capitalize">{roleLabel(m.role)}</div>
            </div>
            {data.can_manage && (
              <Button variant="ghost" size="icon" className="ml-auto h-7 w-7 text-slate-400 hover:text-red-600" disabled={busy} onClick={() => removeMember(m.user_id)} data-testid={`remove-member-${m.user_id}`}>
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        ))}
        {(data.members?.length || 0) === 0 && <p className="text-xs text-slate-400 px-1">No additional members yet — only the PM has access (plus Coordinators & Admins).</p>}
      </div>

      {data.can_manage && (
        <div className="mt-4 flex items-center gap-2">
          <Select value={adding} onValueChange={setAdding}>
            <SelectTrigger className="h-9 flex-1" data-testid="add-member-select"><SelectValue placeholder="Add a workspace member…" /></SelectTrigger>
            <SelectContent>
              {addable.length === 0 ? <div className="px-3 py-2 text-xs text-slate-500">Everyone is already a member</div>
                : addable.map((u) => <SelectItem key={u.user_id} value={u.user_id}>{u.name} <span className="text-slate-400 text-xs ml-1 capitalize">({roleLabel(u.role)})</span></SelectItem>)}
            </SelectContent>
          </Select>
          <Button onClick={addMember} disabled={!adding || busy} className="bg-[#2563EB] hover:bg-[#1d4ed8]" data-testid="add-member-btn">
            <UserPlus className="h-4 w-4 mr-2" />Add
          </Button>
        </div>
      )}
    </Card>
  );
}
